import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { getRoomsUnits, raumbelegungenApi } from '../lib/rooms';

export interface RoomBooking {
  id: string;
  roomId: number;
  title: string;
  teacherName?: string;
  studentName?: string;
  date: string;
  startTime: string;
  endTime: string;
  type: 'lesson' | 'practice' | 'event' | 'maintenance';
  color: string;
  /** Gesetzt bei freien Raumbuchungen — nur die lassen sich hier ändern (T-0092). */
  belegungId?: number;
}

/**
 * Custom Hook zum Laden von Raumbelegungen für einen Zeitraum
 */
export const useRoomBookings = (roomIds: number[], weekDays: Date[]) => {
  const [bookings, setBookings] = useState<RoomBooking[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [refetchCounter, setRefetchCounter] = useState(0);

  useEffect(() => {
    const fetchBookings = async () => {
      if (roomIds.length === 0 || weekDays.length === 0) {
        setBookings([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const start = format(weekDays[0], 'yyyy-MM-dd');
        const end = format(weekDays[weekDays.length - 1], 'yyyy-MM-dd');
        
        const units = await getRoomsUnits(roomIds, start, end);
        
        // Konvertiere Unterrichtseinheiten in RoomBooking-Format
        const converted: RoomBooking[] = units.map(unit => {
          const startDate = new Date(unit.start_zeit);
          const endDate = new Date(unit.end_zeit);
          
          // Titel: Kursname, Fachname oder Fallback
          const title = unit.kurs_name || unit.fach_name || (unit.source_type === 'trial' ? 'Probestunde' : 'Unterricht');
          
          return {
            id: String(unit.id),
            roomId: unit.raum_id || 0,
            title,
            teacherName: unit.lehrer_name || undefined,
            date: format(startDate, 'yyyy-MM-dd'),
            startTime: format(startDate, 'HH:mm'),
            endTime: format(endDate, 'HH:mm'),
            type: unit.source_type === 'trial' ? 'practice' : 'lesson',
            color: unit.status === 'abgesagt' 
              ? 'bg-red-100 border-red-300 text-red-800'
              : unit.status === 'verschoben'
              ? 'bg-yellow-100 border-yellow-300 text-yellow-800'
              : unit.source_type === 'trial'
              ? 'bg-green-100 border-green-300 text-green-800'
              : 'bg-blue-100 border-blue-300 text-blue-800'
          };
        });
        
        // Freie Raumbuchungen dazu. Scheitert das Laden, bleibt der Unterricht sichtbar.
        const roomSet = new Set(roomIds);
        const belegungen = await raumbelegungenApi.list(start, end).catch(() => []);
        const frei: RoomBooking[] = belegungen
          .filter((b) => roomSet.has(b.raum_id) && b.status !== 'cancelled')
          .map((b) => ({
            id: `belegung-${b.id}`,
            belegungId: b.id,
            roomId: b.raum_id,
            title: b.title || 'Raumbuchung',
            // Wandzeit: direkt aus dem Text, ohne Umrechnung in Ortszeit.
            date: b.starts_at.slice(0, 10),
            startTime: b.starts_at.slice(11, 16),
            endTime: b.ends_at.slice(11, 16),
            type: 'event',
            color: 'bg-violet-100 border-violet-300 text-violet-800',
          }));

        setBookings([...converted, ...frei]);
      } catch (err) {
        console.error('Fehler beim Laden der Raumbelegungen:', err);
        setError(err instanceof Error ? err : new Error('Unbekannter Fehler'));
        setBookings([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookings();
  }, [roomIds.join(','), weekDays.map(d => d.getTime()).join(','), refetchCounter]);

  return { bookings, isLoading, error, refetch: () => setRefetchCounter(c => c + 1) };
};


